import React, { Dispatch, SetStateAction } from "react";
import { useContext, useState } from "react";
import { ProductContext } from "../../../../context/product-context/productContext";
import { Grid } from "@mui/material";
import { ProductRefType } from "../../../../context/product-context/types";
import Button from "../../../../ui/widgets/button";
import Typography from "../../../../ui/widgets/typography";
import ProductSelection from "../../../../ui/widgets/product-selection";
import RadioButton from "../../../../ui/widgets/radiobutton";
import Dialog from "../../../../ui/widgets/dialog";
import { Product } from "../../../../context/product-context/product";
import IconButton from "../../../../ui/widgets/button-icon";
import { useMessageSource } from "react-message-source";
import CancelReason from "./cancel-reason";
import { CancelReasonModel } from "./types";
import { CancellationCase } from "../../../../context/cancelApplicationContext";
import { RadioButtonItem } from "../../../../ui/widgets/radiobutton/types";

type SelectedCancelOptionType = "cancel" | "change_product" | null;

export interface CancelBasicAccountApplicationProps {
  cancellationCase: CancellationCase;
  setCancelDialogActive: Dispatch<SetStateAction<boolean>>;
  cancelApplicationCallback: (values: CancelReasonModel) => void;
  switchProductCallback: (product: Product) => void;
}

export default function CancelBasicAccountApplication({
  cancellationCase,
  setCancelDialogActive,
  cancelApplicationCallback,
  switchProductCallback,
}: CancelBasicAccountApplicationProps): React.JSX.Element {
  const [selectedOption, setSelectedOption] =
    useState<SelectedCancelOptionType>("cancel");
  const { findProduct, selectedProduct } = useContext(ProductContext);
  const [selectedProductForCancelling, setSelectedProductForCancelling] =
    useState<Product | null>(selectedProduct);
  const { getMessage } = useMessageSource();

  const radioButtonOptions: RadioButtonItem[] = [
    {
      id: "cancel" as SelectedCancelOptionType,
      label: getMessage(
        "CancelApplication.basic-account-application.cancel.label",
      ),
      value: "cancel",
    },
  ];

  const productCount = selectedProduct?.family.getProductCount({
    areHiddenProductsCounted: !selectedProduct?.shouldBeShown,
  });

  if (productCount > 1) {
    radioButtonOptions.push({
      id: "change_product" as SelectedCancelOptionType,
      label: getMessage(
        "CancelApplication.basic-account-application.change-product.label",
      ),
      value: "change_product",
    });
  }

  let contentJsx = null;
  if (selectedOption === "cancel") {
    contentJsx = (
      <CancelReason
        cancellationCase={cancellationCase}
        cancelApplicationCallback={cancelApplicationCallback}
      />
    );
  } else if (selectedOption === "change_product") {
    contentJsx = (
      <>
        <Typography variant="body1" className="scd-container-base">
          {getMessage(
            "CancelApplication.basic-account-application.change-product.text",
          )}
        </Typography>
        <Grid size={{ xs: 12 }}>
          <ProductSelection
            selectedProduct={selectedProductForCancelling}
            onSelectCallback={(productRef: ProductRefType) => {
              const product = findProduct(productRef);
              setSelectedProductForCancelling(product);
            }}
          />
        </Grid>
        <div className="scd-dialog-footer">
          <Button
            kind={"primary"}
            onClick={() => {
              if (selectedProductForCancelling) {
                switchProductCallback(selectedProductForCancelling);
              }
              setCancelDialogActive(false);
            }}
          >
            {getMessage(
              "CancelApplication.basic-account-application.change-product.button",
            )}
          </Button>
        </div>
      </>
    );
  }

  return (
    <Dialog open={true} maxWidth="lg">
      <div className="scd-dialog-header">
        <Typography variant="h2">
          {getMessage("CancelApplication.basic-account-application.title")}
        </Typography>
        <IconButton
          onClick={() => setCancelDialogActive(false)}
          title="Close"
          icon="close"
        />
      </div>
      <Typography variant="body1">
        {getMessage("CancelApplication.basic-account-application.text")}
      </Typography>
      <div className="scd-container-base">
        <RadioButton
          values={radioButtonOptions}
          selectedValue={selectedOption}
          onClick={(selectedOption: RadioButtonItem) => {
            setSelectedOption(selectedOption.id as SelectedCancelOptionType);
          }}
        />
      </div>
      {contentJsx}
    </Dialog>
  );
}
